import dayjs, { type Dayjs } from 'dayjs';
import {
  actionPlanCalendarYMD,
  formatActionPlanDateDisplay,
  toActionPlanApiDate,
  toActionPlanCalendarDate,
} from '@/utils/dateOnlyForApi';
import { parseDateRangeToDayjsTuple } from '@/components/common/ActionPlan/utils/du-an-utils';

export {
  actionPlanCalendarYMD,
  formatActionPlanDateDisplay,
  toActionPlanApiDate,
  toActionPlanCalendarDate,
};

/** Parse ngày công việc → dayjs (ngày lịch, không lệch timezone). */
export function parseActionPlanDayjs(value: unknown): Dayjs | null {
  const ymd = actionPlanCalendarYMD(value as Date | string | null | undefined);
  if (!ymd) return null;
  const d = dayjs(ymd, 'YYYY-MM-DD', true);
  return d.isValid() ? d : null;
}

/** Khoảng ngày hiển thị "DD/MM/YYYY – DD/MM/YYYY" (thiếu 1 đầu → chỉ đầu còn lại). */
export function formatDateRangeDisplay(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined
): string {
  const from = formatActionPlanDateDisplay(start);
  const to = formatActionPlanDateDisplay(end);
  if (from && to) return `${from} – ${to}`;
  return from || to || '';
}

/** Gợi ý bộ lọc ngày trên header Home ("DD/MM/YYYY → DD/MM/YYYY"). */
export function formatDateFilterHint(dateRange?: string | null): string {
  return dateRange ? dateRange.replace(' - ', ' → ') : 'Không lọc theo ngày';
}

/** dayjs / Date user chọn → Date local midnight đúng ngày lịch. */
export function actionPlanDateFromUserInput(value: unknown): Date | null {
  if (value == null || value === '') return null;
  const ymd = dayjs(value as dayjs.ConfigType).format('YYYY-MM-DD');
  return toActionPlanCalendarDate(ymd);
}

type TableRecordLike = {
  congViec?: { ngayBatDau?: Date | string | null; ngayKetThuc?: Date | string | null };
  childs?: TableRecordLike[];
};

function normalizeCongViecCalendarDates(cv: NonNullable<TableRecordLike['congViec']>) {
  return {
    ...cv,
    ngayBatDau:
      cv.ngayBatDau != null && cv.ngayBatDau !== ''
        ? toActionPlanCalendarDate(cv.ngayBatDau) ?? cv.ngayBatDau
        : cv.ngayBatDau,
    ngayKetThuc:
      cv.ngayKetThuc != null && cv.ngayKetThuc !== ''
        ? toActionPlanCalendarDate(cv.ngayKetThuc) ?? cv.ngayKetThuc
        : cv.ngayKetThuc,
  };
}

/** Chuẩn hóa ngày lịch trên cây bảng (copy/paste/merge response). */
export function normalizeTableRecordSubtreeDates<T extends TableRecordLike>(node: T): T {
  const childs = (node.childs ?? []).map((child) => normalizeTableRecordSubtreeDates(child));
  if (!node.congViec) return { ...node, childs } as T;
  return {
    ...node,
    congViec: normalizeCongViecCalendarDates(node.congViec),
    childs,
  } as T;
}

/**
 * Format dateRange from "DD/MM/YYYY - DD/MM/YYYY" or "DD-MM-YYYY - DD-MM-YYYY" to "DD-MM-YYYY-DD-MM-YYYY" or "nulldate"
 */
export const formatDateRangeForUrl = (dateRange: string | null): string => {
  if (!dateRange) {
    return 'nulldate';
  }
  // Format từ state có thể là: "DD/MM/YYYY - DD/MM/YYYY" hoặc "DD-MM-YYYY - DD-MM-YYYY"
  // Cần convert thành: "DD-MM-YYYY-DD-MM-YYYY" (ví dụ: "30-11-2025-18-12-2025")
  const [startDate, endDate] = dateRange.split(' - ');
  if (!startDate || !endDate) {
    return 'nulldate';
  }

  // Hỗ trợ cả 2 format: có dấu / hoặc có dấu -
  let startDay: string, startMonth: string, startYear: string;
  let endDay: string, endMonth: string, endYear: string;

  if (startDate.includes('/')) {
    // Format: DD/MM/YYYY
    [startDay, startMonth, startYear] = startDate.split('/');
  } else if (startDate.includes('-')) {
    // Format: DD-MM-YYYY
    [startDay, startMonth, startYear] = startDate.split('-');
  } else {
    return 'nulldate';
  }

  if (endDate.includes('/')) {
    // Format: DD/MM/YYYY
    [endDay, endMonth, endYear] = endDate.split('/');
  } else if (endDate.includes('-')) {
    // Format: DD-MM-YYYY
    [endDay, endMonth, endYear] = endDate.split('-');
  } else {
    return 'nulldate';
  }

  if (!startDay || !startMonth || !startYear || !endDay || !endMonth || !endYear) {
    return 'nulldate';
  }
  return `${startDay}-${startMonth}-${startYear}-${endDay}-${endMonth}-${endYear}`;
};

/**
 * Ngày mặc định cho việc mới theo bộ lọc ngày đang chọn ("DD/MM/YYYY - DD/MM/YYYY").
 * Có cha → phần giao với khoảng của cha (BE bắt con nằm trong cha).
 * Trả `null` khi không lọc / không parse được / không giao với cha → người gọi dùng mặc định cũ.
 * Ngày trả về = 0h local đúng ngày lịch (như `toActionPlanCalendarDate`).
 */
export function resolveNewTaskDatesFromFilter(
  filter: string | null | undefined,
  parent?: { ngayBatDau?: unknown; ngayKetThuc?: unknown } | null
): [Date, Date] | null {
  const range = parseDateRangeToDayjsTuple(filter);
  if (!range) return null;
  let [start, end] = range.map((d) => d.startOf('day'));
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return null;
  if (parent) {
    const ps = parseActionPlanDayjs(parent.ngayBatDau);
    const pe = parseActionPlanDayjs(parent.ngayKetThuc);
    if (ps && ps.isAfter(start)) start = ps;
    if (pe && pe.isBefore(end)) end = pe;
    if (start.isAfter(end)) return null;
  }
  return [start.toDate(), end.toDate()];
}
